"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { getCareerBundleActivationPath } from "@/lib/payments/career-bundle-destination";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { clearGuestCheckout } from "@/components/offer/guest-checkout-storage";
import {
  resolveGuestClaimErrorKind,
  type GuestClaimErrorKind,
} from "@/lib/payments/guest-claim-errors";
import { createClient } from "@/lib/supabase/client";

const CLAIM_TOKEN_COOKIE = "hyred_claim_token";

function getClaimToken(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${CLAIM_TOKEN_COOKIE}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function clearClaimToken(): void {
  document.cookie = `${CLAIM_TOKEN_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

export function useGuestClaim({
  locale,
  successMessage,
}: {
  locale: string;
  successMessage: string;
}) {
  const router = useRouter();
  const [claimToken, setClaimToken] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<GuestClaimErrorKind | null>(
    null,
  );
  const [isClaiming, setIsClaiming] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);

  const claim = useCallback(
    async (token: string) => {
      if (isClaiming) {
        return;
      }

      setIsClaiming(true);
      setClaimError(null);

      try {
        const response = await fetch("/api/payment/claim", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ claimToken: token }),
        });

        if (response.ok) {
          clearClaimToken();
          clearGuestCheckout();
          toast.success(successMessage);
          router.replace(getCareerBundleActivationPath(locale));
          return;
        }

        const payload: unknown = await response.json().catch(() => null);
        setClaimError(resolveGuestClaimErrorKind(response.status, payload));
      } catch {
        setClaimError("temporary");
      } finally {
        setIsClaiming(false);
      }
    },
    [isClaiming, locale, router, successMessage],
  );

  useMountEffect(() => {
    const token = getClaimToken();
    if (!token) {
      return;
    }

    setClaimToken(token);
    void claim(token);
  });

  const retry = useCallback(() => {
    if (claimToken) {
      void claim(claimToken);
    }
  }, [claim, claimToken]);

  const switchAccount = useCallback(async () => {
    setIsSwitchingAccount(true);
    try {
      const supabase = createClient();
      await supabase.auth.signOut();
      const offerPath = `/${locale}/dashboard/offer`;
      router.push(
        `/${locale}/sign-in?redirect=${encodeURIComponent(offerPath)}`,
      );
      router.refresh();
    } catch {
      setClaimError("temporary");
      setIsSwitchingAccount(false);
    }
  }, [locale, router]);

  return {
    claimError,
    hasPendingClaim: Boolean(claimToken),
    isClaiming: isClaiming || isSwitchingAccount,
    retry,
    switchAccount,
  };
}
